// Iter47-A — Admin → Tenant Types (master data CRUD, 18 system rows across 5 layers §2.4).
import React, { useMemo, useState } from "react";
import { Building2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useStore } from "../store";
import { useT } from "../lib/i18n";
import {
  useTenantTypes,
  useCreateTenantType,
  useUpdateTenantType,
  useDeleteTenantType,
  useIncidentCategories,
} from "../api/sitroom";
import {
  mono, inputStyle, Label, NameRow, SystemBadge, RowActions, NewRowButton, SaveCancelButtons, pickName,
} from "../components/MasterDataAtoms";

const LAYERS = [1, 2, 3, 4, 5];
const LAYER_LABELS = {
  1: "L1 · Production Chain",
  2: "L2 · Engineering & Quality",
  3: "L3 · Logistics",
  4: "L4 · Warehousing & VAS",
  5: "L5 · Indirect / Support",
};

export default function AdminTenantTypes() {
  const { user } = useAuth();
  const { locale } = useT();
  const addToast = useStore((s) => s.addToast);
  const { data: rows = [], isLoading } = useTenantTypes();
  const { data: categories = [] } = useIncidentCategories();
  const createMut = useCreateTenantType();
  const updateMut = useUpdateTenantType();
  const deleteMut = useDeleteTenantType();
  const isPlatform = user?.role === "platform_admin";

  const [editing, setEditing] = useState(null);

  // Group rows by layer for the layered display.
  const byLayer = useMemo(() => {
    const map = Object.fromEntries(LAYERS.map((l) => [l, []]));
    rows.forEach((r) => { (map[r.layer] || (map[r.layer] = [])).push(r); });
    return map;
  }, [rows]);

  const onSave = async (key, body, isNew) => {
    try {
      if (isNew) {
        await createMut.mutateAsync(body);
        addToast({ type: "success", title: "Tenant type created", body: body.names?.en });
      } else {
        await updateMut.mutateAsync({ key, body });
        addToast({ type: "success", title: "Tenant type updated", body: body.names?.en || key });
      }
      setEditing(null);
    } catch (e) {
      addToast({ type: "error", title: "Save failed", body: e?.response?.data?.detail || e.message });
    }
  };

  const onDelete = async (row) => {
    if (!window.confirm(`Delete tenant type "${pickName(row, locale)}"?`)) return;
    try {
      await deleteMut.mutateAsync(row.key);
      addToast({ type: "success", title: "Tenant type deleted" });
    } catch (e) {
      addToast({ type: "error", title: "Delete failed", body: e?.response?.data?.detail || e.message });
    }
  };

  return (
    <div data-testid="admin-tenant-types">
      <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
        <div style={mono(10, "var(--dimmer, #9198b0)", 700, "0.8px")} className="uppercase">
          Tenant Types · {rows.length} · 5 supply-chain layers
        </div>
        {isPlatform && <NewRowButton onClick={() => setEditing("new")} label="New tenant type" testId="tenant-type-new-btn" />}
      </div>

      {isLoading && <div style={mono(11, "var(--dimmer, #9198b0)")}>Loading…</div>}

      <div className="space-y-4">
        {LAYERS.map((layer) => (
          <div key={layer}>
            <div className="mb-2 px-2 py-1 rounded inline-block"
              style={{ background: "rgba(24,95,165,0.12)", color: "#9ec3ec", ...mono(9, undefined, 700, "0.6px"), textTransform: "uppercase" }}>
              {LAYER_LABELS[layer]} · {(byLayer[layer] || []).length}
            </div>
            <div className="space-y-2">
              {(byLayer[layer] || []).map((row) => (
                <Row key={row.key} row={row} isPlatform={isPlatform} locale={locale} categories={categories}
                  editing={editing === row.key}
                  onEdit={() => setEditing(row.key)}
                  onCancel={() => setEditing(null)}
                  onSave={(body) => onSave(row.key, body, false)}
                  onDelete={() => onDelete(row)} />
              ))}
            </div>
          </div>
        ))}
        {editing === "new" && (
          <Row row={null} isPlatform={isPlatform} locale={locale} categories={categories}
            editing={true}
            onCancel={() => setEditing(null)}
            onSave={(body) => onSave(null, body, true)} />
        )}
      </div>
    </div>
  );
}

const Row = ({ row, isPlatform, locale, categories, editing, onEdit, onCancel, onSave, onDelete }) => {
  const [key, setKey] = useState(row?.key || "");
  const [names, setNames] = useState(row?.names || { en: "", de: "", hu: "" });
  const [layer, setLayer] = useState(row?.layer ?? 1);
  const [incidentRole, setIncidentRole] = useState(row?.incident_role || "");
  const [allowedCats, setAllowedCats] = useState(row?.allowed_incident_categories || []);

  const toggleCat = (k) => setAllowedCats((cur) => (cur.includes(k) ? cur.filter((x) => x !== k) : [...cur, k]));

  const handleSave = () => {
    if (!names.en?.trim()) return;
    const body = {
      names: { en: names.en?.trim(), de: names.de?.trim() || names.en?.trim(), hu: names.hu?.trim() || names.en?.trim() },
      layer: parseInt(layer, 10),
      incident_role: incidentRole?.trim() || null,
      allowed_incident_categories: allowedCats,
    };
    if (!row) body.key = key.trim();
    onSave(body);
  };

  if (editing) {
    return (
      <div className="rounded-md p-4"
        style={{ background: "var(--bg-surface, #0d0f18)", border: "1px solid rgba(24,95,165,0.5)" }}
        data-testid={row ? `tenant-type-edit-${row.key}` : "tenant-type-edit-new"}>
        {!row && (
          <div className="mb-3">
            <Label>Key (lowercase, alpha-numeric)</Label>
            <input value={key} onChange={(e) => setKey(e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, ""))}
              placeholder="e.g. custom_partner" style={inputStyle} data-testid="tenant-type-edit-key" />
          </div>
        )}
        <NameRow names={names} onChange={setNames} testIdPrefix="tenant-type-edit" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-3">
          <div>
            <Label>Supply-chain layer</Label>
            <select value={layer} onChange={(e) => setLayer(e.target.value)} style={inputStyle} data-testid="tenant-type-edit-layer">
              {LAYERS.map((l) => <option key={l} value={l}>{LAYER_LABELS[l]}</option>)}
            </select>
          </div>
          <div>
            <Label>Typical incident role</Label>
            <input value={incidentRole} onChange={(e) => setIncidentRole(e.target.value)} placeholder="e.g. Quality lock responder" style={inputStyle} data-testid="tenant-type-edit-role" />
          </div>
        </div>
        <div className="mb-3">
          <Label>Allowed incident categories</Label>
          <div className="flex flex-wrap gap-1">
            {categories.map((c) => {
              const on = allowedCats.includes(c.key);
              return (
                <button key={c.key} type="button" onClick={() => toggleCat(c.key)}
                  className="px-2 py-0.5 rounded"
                  style={{
                    background: on ? "rgba(24,95,165,0.18)" : "transparent",
                    border: `1px solid ${on ? "rgba(24,95,165,0.55)" : "rgba(255,255,255,0.10)"}`,
                    color: on ? "#9ec3ec" : "var(--text-dim, #c5cad8)",
                    ...mono(9, undefined, 600, "0.3px"), cursor: "pointer",
                  }}
                  data-testid={`tenant-type-edit-cat-${c.key}`}
                  aria-pressed={on}>
                  {pickName(c, locale)}
                </button>
              );
            })}
          </div>
        </div>
        <SaveCancelButtons onSave={handleSave} onCancel={onCancel} testIdPrefix="tenant-type-edit" />
      </div>
    );
  }

  return (
    <div className="rounded-md p-3"
      style={{ background: "var(--bg-surface, #0d0f18)", border: `1px solid ${row.system ? "rgba(243,177,74,0.25)" : "rgba(255,255,255,0.10)"}` }}
      data-testid={`tenant-type-row-${row.key}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <Building2 size={14} color="#9ec3ec" />
          <span style={{ fontFamily: "Syne, sans-serif", fontWeight: 700, fontSize: 14, color: "var(--text, #f0f2f8)" }}>
            {pickName(row, locale)}
          </span>
          <span style={mono(10, "var(--dimmer, #9198b0)")}>key={row.key}</span>
          {row.system && <SystemBadge />}
        </div>
        {isPlatform && (
          <RowActions row={row} onEdit={onEdit} onDelete={onDelete} canDelete={!row.system} testIdPrefix="tenant-type" />
        )}
      </div>
      {row.incident_role && (
        <div className="mt-1.5" style={mono(11, "var(--text-dim, #c5cad8)")}>
          {row.incident_role}
        </div>
      )}
      {(row.allowed_incident_categories || []).length > 0 && (
        <div className="mt-1.5 flex flex-wrap gap-1">
          {(row.allowed_incident_categories || []).map((c) => (
            <span key={c} className="px-1.5 py-0.5 rounded"
              style={{ background: "rgba(255,255,255,0.04)", color: "var(--text-dim, #c5cad8)", ...mono(9, undefined, 600, "0.3px") }}>
              {c}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};
